import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  toast,
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Copy, Users } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import NotFound from '@Components/NotFound';
import PartnerReferralCodeSheet from '@Components/PartnerReferralCodes/PartnerReferralCodeSheet';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import ShowFor from '@Components/ShowFor';
import { usePartnerReferralCode } from '@Hooks/usePartnerReferralCode';
import { usePartnerReferralCodeSubscriptions } from '@Hooks/usePartnerReferralCodeSubscriptions';
import type { PartnerReferralCodeSubscription } from '@Types/partnerReferralCode';
import { formatPhoneNumberFriendlyDisplay } from '@Utils/utils';
import { useSecret } from '../../stores/secretsStore';

const MEMBERSHIP_APP_URL_SECRET = 'MEMBERSHIP_APP_URL';
const SHARE_LINK_PATH = '/web-register/step-one';
const SHARE_LINK_CODE_PARAM = 'code';

const getMemberName = (row: PartnerReferralCodeSubscription) => {
  const profile = row.fish?.fishProfiles?.[0];
  const name = [profile?.fName, profile?.lName].filter(Boolean).join(' ');
  return name || '-';
};

const signupColumnConfigs: ColumnConfig<PartnerReferralCodeSubscription>[] = [
  {
    id: 'name',
    headerLabel: 'Member',
    accessor: (row) => getMemberName(row),
    type: 'text',
    size: 180,
    enableSorting: false,
  },
  {
    id: 'phoneNumber',
    headerLabel: 'Phone',
    accessor: (row) =>
      row.fish?.phoneNumber
        ? formatPhoneNumberFriendlyDisplay(row.fish.phoneNumber)
        : '-',
    type: 'text',
    size: 140,
    enableSorting: false,
  },
  {
    id: 'subscriptionTier',
    headerLabel: 'Tier',
    accessor: (row) => row.subscriptionTier?.title ?? '-',
    type: 'text',
    size: 160,
    enableSorting: false,
  },
  {
    id: 'registrationCode',
    headerLabel: 'Invite Code',
    accessor: (row) => row.registrationCode?.code ?? '-',
    type: 'text',
    size: 160,
    enableSorting: false,
  },
  {
    id: 'createdAt',
    headerLabel: 'Signed Up',
    accessor: (row) =>
      DateTime.fromISO(row.createdAt).toLocaleString(DateTime.DATETIME_MED),
    type: 'text',
    size: 180,
    enableSorting: false,
  },
];

function PartnerReferralCodeSignupList({
  partnerReferralCodeId,
}: {
  partnerReferralCodeId: string;
}) {
  const navigate = useNavigate();

  const controller = useResourceTableController<PartnerReferralCodeSubscription>(
    {
      columnsConfig: signupColumnConfigs,
      defaultSort: {
        id: 'createdAt',
        desc: true,
      },
      buildFilterParams: () => ({}),
      initialPageSize: 10,
    },
  );

  const {
    data: signups,
    status,
    isError,
    error,
  } = usePartnerReferralCodeSubscriptions({
    id: partnerReferralCodeId,
    searchParams: {
      'page[number]': controller.searchParams['page[number]'],
      'page[size]': controller.searchParams['page[size]'],
    },
  });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error ? error.message : 'Failed to load signups',
      );
    }
  }, [isError, error]);

  return (
    <ResourceTableView<PartnerReferralCodeSubscription>
      label="Signups"
      controller={controller}
      data={signups?.data ?? []}
      meta={signups?.meta}
      status={status}
      summaryIcon={<Users className="h-4 w-4 text-zinc-500" />}
      summaryLabel="Total Signups: "
      onRowClick={(row) => navigate(`/members/${row.fishId}`)}
    />
  );
}

export default function PartnerReferralCodeDetails() {
  const { id } = useParams();
  const membershipAppUrl = useSecret(MEMBERSHIP_APP_URL_SECRET);
  const [sheetOpen, setSheetOpen] = useState(false);

  const {
    data: partnerReferralCode,
    status,
    isError,
  } = usePartnerReferralCode({ id: id ?? '', enabled: !!id });

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !partnerReferralCode) {
    return <div>Loading...</div>;
  }

  const sharePath = `${SHARE_LINK_PATH}?${new URLSearchParams({
    [SHARE_LINK_CODE_PARAM]: partnerReferralCode.code,
  }).toString()}`;
  const shareLink = membershipAppUrl
    ? `${membershipAppUrl.replace(/\/+$/, '')}${sharePath}`
    : sharePath;
  const linkedRegistrationCodes = partnerReferralCode.registrationCodes ?? [];

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      if (membershipAppUrl) {
        toastSuccess('Share link copied.');
      } else {
        toast(
          `${MEMBERSHIP_APP_URL_SECRET} is not set for this tenant, so only the path was copied. Prefix it with the membership app address.`,
        );
      }
    } catch {
      toastError('Unable to copy the share link.');
    }
  };

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/' },
          { name: 'Partner Codes', href: '/partner-referral-codes' },
          {
            name: partnerReferralCode.code,
            href: `/partner-referral-codes/${partnerReferralCode.id}`,
          },
        ]}
      />

      <div className="mt-8 mb-4 flex items-end">
        <Heading>{partnerReferralCode.code}</Heading>
        <p className="mx-2 mb-1 text-sm font-bold text-zinc-500">
          PARTNER CODE
        </p>
        <div className="ml-auto flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={handleCopyShareLink}>
            <Copy className="mr-2 h-4 w-4" />
            Copy Share Link
          </Button>
          <ShowFor permissions={['REGISTRATION_CODES_WRITE']}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSheetOpen(true)}
            >
              Edit
            </Button>
          </ShowFor>
        </div>
      </div>

      <div className="flex flex-col space-y-8">
        <div>
          <div className="mb-2">
            <Heading level="h3">Details</Heading>
          </div>
          <Card>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
                <div className="col-span-2 sm:col-span-3">
                  <div className="text-sm font-bold text-zinc-500">
                    Description
                  </div>
                  {partnerReferralCode.description || (
                    <span className="text-zinc-400">No description</span>
                  )}
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <div className="text-sm font-bold text-zinc-500">
                    Share Link
                  </div>
                  <span className="font-mono break-all">{shareLink}</span>
                </div>
                <div>
                  <div className="text-sm font-bold text-zinc-500">
                    Created On
                  </div>
                  {DateTime.fromISO(
                    partnerReferralCode.createdAt,
                  ).toLocaleString(DateTime.DATETIME_MED)}
                </div>
                <div>
                  <div className="text-sm font-bold text-zinc-500">
                    Created By
                  </div>
                  {partnerReferralCode.createdByUser?.name || '-'}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <div className="mb-2">
            <Heading level="h3">Linked Invite Codes</Heading>
          </div>
          <Card>
            <CardContent>
              {linkedRegistrationCodes.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Promo Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedRegistrationCodes.map((registrationCode) => (
                      <TableRow key={registrationCode.id}>
                        <TableCell>
                          <Link
                            to={`/invite-codes/${registrationCode.id}`}
                            className="text-primary font-mono hover:underline"
                          >
                            {registrationCode.code}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {registrationCode.promoDetails?.length ? (
                            registrationCode.promoDetails.map(
                              (detail, index) => <div key={index}>{detail}</div>,
                            )
                          ) : (
                            <span className="text-zinc-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-4 text-center text-sm text-zinc-500">
                  No active invite codes are linked, so prospects entering
                  this partner code are told it is invalid.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <PartnerReferralCodeSignupList
          partnerReferralCodeId={partnerReferralCode.id}
        />
      </div>

      <PartnerReferralCodeSheet
        partnerReferralCode={partnerReferralCode}
        isOpen={sheetOpen}
        setIsOpen={setSheetOpen}
        onSuccess={() =>
          toastSuccess(`"${partnerReferralCode.code}" has been updated.`)
        }
      />
    </div>
  );
}
